import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Divider,
  FormHelperText,
  Alert,
  CircularProgress,
  Autocomplete,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import projectService from '../../services/project.service';
import { Project, JobType, WorkMode, Visibility, SalaryPeriod, WeekDay, CustomQuestion } from '../../types/Project';

const AddProject: React.FC = () => {
  const { id: companyId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  // Form state
  const [formData, setFormData] = useState<Partial<Project>>({
    project_name: '',
    description: '',
    job_type: 'full_time' as JobType,
    work_mode: 'remote' as WorkMode,
    experience: {
      min: 0,
      max: 5,
    },
    salary: {
      min: 0,
      max: 0,
      currency: 'USD',
      period: 'yearly' as SalaryPeriod,
      is_displayed: true
    },
    screening_interview_type: 'on_website' as 'on_call' | 'on_website' | 'both',
    work_schedule: {
      start_time: '',
      end_time: '',
      timezone: 'UTC'
    },
    notice_period: '',
    other_requirements: '',
    application_form_fields: {
      resume: { required: true },
      cover_letter: { required: false },
      custom_questions: []
    },
    visibility: 'public' as Visibility,
    status: 'draft',
    must_have_skills: [],
    good_to_have_skills: [],
    key_responsibilities: [],
    why_join_us: [],
    qualifications: [],
  });

  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  // Field state
  const [skillInput, setSkillInput] = useState<string>('');
  const [responsibilityInput, setResponsibilityInput] = useState<string>('');
  const [benefitInput, setBenefitInput] = useState<string>('');
  const [qualificationInput, setQualificationInput] = useState<string>('');
  
  // Validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Handle form input changes
  const handleChange = (e: any) => {
    const { name, value } = e.target;
    
    if (name) {
      // Handle nested fields
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData((prev: any) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      
      // Clear error for this field if it exists
      if (formErrors[name]) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Handle skills, responsibilities, benefits, qualifications
  const handleAddSkill = () => {
    if (skillInput.trim() && !formData.must_have_skills?.includes(skillInput.trim())) {
      setFormData(prev => ({
        ...prev,
        must_have_skills: [...(prev.must_have_skills || []), skillInput.trim()],
      }));
      setSkillInput('');
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setFormData(prev => ({
      ...prev,
      must_have_skills: prev.must_have_skills?.filter(s => s !== skill),
    }));
  };

  const handleAddResponsibility = () => {
    if (responsibilityInput.trim() && !formData.key_responsibilities?.includes(responsibilityInput.trim())) {
      setFormData(prev => ({
        ...prev,
        key_responsibilities: [...(prev.key_responsibilities || []), responsibilityInput.trim()],
      }));
      setResponsibilityInput('');
    }
  };

  const handleRemoveResponsibility = (responsibility: string) => {
    setFormData(prev => ({
      ...prev,
      key_responsibilities: prev.key_responsibilities?.filter(r => r !== responsibility),
    }));
  };

  const handleAddBenefit = () => {
    if (benefitInput.trim() && !formData.why_join_us?.includes(benefitInput.trim())) {
      setFormData(prev => ({
        ...prev,
        why_join_us: [...(prev.why_join_us || []), benefitInput.trim()],
      }));
      setBenefitInput('');
    }
  };

  const handleRemoveBenefit = (benefit: string) => {
    setFormData(prev => ({
      ...prev,
      why_join_us: prev.why_join_us?.filter(b => b !== benefit),
    }));
  };

  const handleAddQualification = () => {
    if (qualificationInput.trim() && !formData.qualifications?.includes(qualificationInput.trim())) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...(prev.qualifications || []), qualificationInput.trim()],
      }));
      setQualificationInput('');
    }
  };

  const handleRemoveQualification = (qualification: string) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications?.filter(q => q !== qualification),
    }));
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.project_name) {
      errors.project_name = 'Project name is required';
    }
    
    if (!formData.description) {
      errors.description = 'Description is required';
    }
    
    if (!formData.must_have_skills?.length) {
      errors.must_have_skills = 'At least one required skill is needed';
    }
    
    if (!formData.key_responsibilities?.length) {
      errors.key_responsibilities = 'At least one key responsibility is needed';
    }
    
    if (!formData.screening_interview_type) {
      errors.screening_interview_type = 'Screening interview type is required';
    }
    
    if (formData.salary?.is_displayed) {
      if (!formData.salary.min || !formData.salary.max) {
        errors.salary = 'Both minimum and maximum salary are required when salary is displayed';
      } else if (formData.salary.min > formData.salary.max) {
        errors.salary = 'Maximum salary must be greater than or equal to minimum salary';
      }
    }
    
    if (formData.work_schedule?.start_time && !formData.work_schedule.end_time) {
      errors['work_schedule.end_time'] = 'End time is required when start time is provided';
    }
    
    if (formData.work_schedule?.end_time && !formData.work_schedule.start_time) {
      errors['work_schedule.start_time'] = 'Start time is required when end time is provided';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!companyId) {
      setError('Company ID is missing');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await projectService.createProject(companyId, formData);
      setSuccess(true);
      
      // Redirect back to company details page after success
      setTimeout(() => {
        navigate(`/companies/${companyId}`);
      }, 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create project');
      console.error('Error creating project:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate(`/companies/${companyId}`)} 
          sx={{ mr: 1 }}
          aria-label="Back to company details"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Add New Project
        </Typography>
      </Box>
      
      {/* Error/Success message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Project created successfully!
        </Alert>
      )}
      
      {/* Project form */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          mb: 4
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Project Name"
                    name="project_name"
                    value={formData.project_name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={!!formErrors.project_name}
                    helperText={formErrors.project_name}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                    required
                    error={!!formErrors.description}
                    helperText={formErrors.description}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Job Type</InputLabel>
                    <Select
                      name="job_type"
                      value={formData.job_type}
                      onChange={handleChange}
                      label="Job Type"
                    >
                      <MenuItem value="full_time">Full Time</MenuItem>
                      <MenuItem value="part_time">Part Time</MenuItem>
                      <MenuItem value="contract">Contract</MenuItem>
                      <MenuItem value="freelance">Freelance</MenuItem>
                      <MenuItem value="internship">Internship</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Work Mode</InputLabel>
                    <Select
                      name="work_mode"
                      value={formData.work_mode}
                      onChange={handleChange}
                      label="Work Mode"
                    >
                      <MenuItem value="remote">Remote</MenuItem>
                      <MenuItem value="on_site">On Site</MenuItem>
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Location"
                    name="location"
                    value={formData.location || ''}
                    onChange={handleChange}
                    fullWidth
                    placeholder="e.g., San Francisco, CA"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      label="Status"
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="paused">Paused</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Visibility</InputLabel>
                    <Select
                      name="visibility"
                      value={formData.visibility}
                      onChange={handleChange}
                      label="Visibility"
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="company_only">Company Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Experience */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Experience Requirements
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Minimum Experience (years)"
                    name="experience.min"
                    type="number"
                    value={formData.experience?.min}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Maximum Experience (years)"
                    name="experience.max"
                    type="number"
                    value={formData.experience?.max}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Salary */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Salary Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Minimum Salary"
                    name="salary.min"
                    type="number"
                    value={formData.salary?.min}
                    onChange={handleChange}
                    fullWidth
                    error={!!formErrors.salary}
                    helperText={formErrors.salary}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Maximum Salary"
                    name="salary.max"
                    type="number"
                    value={formData.salary?.max}
                    onChange={handleChange}
                    fullWidth
                    error={!!formErrors.salary}
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Currency"
                    name="salary.currency"
                    value={formData.salary?.currency || 'USD'}
                    onChange={handleChange}
                    fullWidth
                    select
                  >
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                    <MenuItem value="GBP">GBP (£)</MenuItem>
                    <MenuItem value="INR">INR (₹)</MenuItem>
                    <MenuItem value="CAD">CAD ($)</MenuItem>
                    <MenuItem value="AUD">AUD ($)</MenuItem>
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Salary Period</InputLabel>
                    <Select
                      name="salary.period"
                      value={formData.salary?.period || 'yearly'}
                      onChange={handleChange}
                      label="Salary Period"
                    >
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Display Salary</InputLabel>
                    <Select
                      name="salary.is_displayed"
                      value={formData.salary?.is_displayed ? "true" : "false"}
                      onChange={(e) => {
                        setFormData((prev: any) => ({
                          ...prev,
                          salary: {
                            ...prev.salary,
                            is_displayed: e.target.value === "true",
                          },
                        }));
                      }}
                      label="Display Salary"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Screening Interview Type */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Screening Interview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!formErrors.screening_interview_type}>
                    <InputLabel>Interview Type</InputLabel>
                    <Select
                      name="screening_interview_type"
                      value={formData.screening_interview_type || 'on_website'}
                      onChange={handleChange}
                      label="Interview Type"
                      required
                    >
                      <MenuItem value="on_call">On Call</MenuItem>
                      <MenuItem value="on_website">On Website</MenuItem>
                      <MenuItem value="both">Both</MenuItem>
                    </Select>
                    {formErrors.screening_interview_type && (
                      <FormHelperText>{formErrors.screening_interview_type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Work Schedule */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Work Schedule
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="work_schedule.start_time"
                    type="time"
                    value={formData.work_schedule?.start_time || ''}
                    onChange={handleChange}
                    fullWidth
                    error={!!formErrors['work_schedule.start_time']}
                    helperText={formErrors['work_schedule.start_time']}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="work_schedule.end_time"
                    type="time"
                    value={formData.work_schedule?.end_time || ''}
                    onChange={handleChange}
                    fullWidth
                    error={!!formErrors['work_schedule.end_time']}
                    helperText={formErrors['work_schedule.end_time']}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Time Zone"
                    name="work_schedule.timezone"
                    value={formData.work_schedule?.timezone || 'UTC'}
                    onChange={handleChange}
                    fullWidth
                    select
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="EST">EST</MenuItem>
                    <MenuItem value="CST">CST</MenuItem>
                    <MenuItem value="MST">MST</MenuItem>
                    <MenuItem value="PST">PST</MenuItem>
                    <MenuItem value="IST">IST</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Notice Period */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Notice Period
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Notice Period"
                    name="notice_period"
                    value={formData.notice_period || ''}
                    onChange={handleChange}
                    fullWidth
                    placeholder="e.g., 2 weeks, 1 month"
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Skills */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Skills
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Required Skills
                  </Typography>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      placeholder="Add a required skill"
                      fullWidth
                      error={!!formErrors.must_have_skills}
                      helperText={formErrors.must_have_skills}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddSkill}
                      sx={{ ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.must_have_skills?.map((skill) => (
                      <Chip
                        key={skill}
                        label={skill}
                        onDelete={() => handleRemoveSkill(skill)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Responsibilities */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Key Responsibilities
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={responsibilityInput}
                      onChange={(e) => setResponsibilityInput(e.target.value)}
                      placeholder="Add a key responsibility"
                      fullWidth
                      error={!!formErrors.key_responsibilities}
                      helperText={formErrors.key_responsibilities}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddResponsibility();
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddResponsibility}
                      sx={{ ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {formData.key_responsibilities?.map((responsibility, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {responsibility}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveResponsibility(responsibility)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Benefits */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Why Join Us
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={benefitInput}
                      onChange={(e) => setBenefitInput(e.target.value)}
                      placeholder="Add a benefit"
                      fullWidth
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddBenefit();
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddBenefit}
                      sx={{ ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {formData.why_join_us?.map((benefit, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {benefit}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveBenefit(benefit)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Qualifications */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Qualifications
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <TextField
                      value={qualificationInput}
                      onChange={(e) => setQualificationInput(e.target.value)}
                      placeholder="Add a qualification"
                      fullWidth
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddQualification();
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleAddQualification}
                      sx={{ ml: 1 }}
                    >
                      Add
                    </Button>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    {formData.qualifications?.map((qualification, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {qualification}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveQualification(qualification)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Other Requirements */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Other Requirements
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Other Requirements"
                    name="other_requirements"
                    value={formData.other_requirements || ''}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Enter any additional requirements for candidates..."
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Application Form Fields */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Application Form Fields
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Resume Required</InputLabel>
                    <Select
                      name="application_form_fields.resume.required"
                      value={(formData.application_form_fields?.resume?.required ?? true) ? "true" : "false"}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          application_form_fields: {
                            ...prev.application_form_fields,
                            resume: {
                              ...prev.application_form_fields?.resume,
                              required: e.target.value === "true",
                            },
                          },
                        }));
                      }}
                      label="Resume Required"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Cover Letter Required</InputLabel>
                    <Select
                      name="application_form_fields.cover_letter.required"
                      value={(formData.application_form_fields?.cover_letter?.required ?? false) ? "true" : "false"}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          application_form_fields: {
                            ...prev.application_form_fields,
                            cover_letter: {
                              ...prev.application_form_fields?.cover_letter,
                              required: e.target.value === "true",
                            },
                          },
                        }));
                      }}
                      label="Cover Letter Required"
                    >
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Custom Questions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    You can add custom questions to the application form. These will be presented to candidates when they apply.
                  </Typography>
                  
                  {/* Add custom question form - Future enhancement */}
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    disabled
                  >
                    Add Custom Question (Coming Soon)
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Form Actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate(`/companies/${companyId}`)}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AddProject;